import React, { useState, useEffect } from "react";
import { Card, ListGroup, Button, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function DraftSubmissions({ caseId, studentNumber }) {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDrafts();
    // eslint-disable-next-line
  }, [caseId, studentNumber]);

  function loadDrafts() {
    setLoading(true);
    // TODO: Replace with actual API endpoint when backend is ready
    axios
      .get(`/api/student/${studentNumber}/case/${caseId}/drafts`, {
        withCredentials: false,
      })
      .then((response) => {
        setDrafts(response.data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading drafts:", err);
        // For now, use mock data
        setDrafts([]);
        setLoading(false);
      });
  }

  function handleCreateNew() {
    navigate(`/student/${studentNumber}/case/${caseId}/draft/new`);
  }

  function handleEditDraft(draft) {
    navigate(`/student/${studentNumber}/case/${caseId}/draft/${draft.id}`);
  }

  function handleDeleteDraft(draftId) {
    if (!window.confirm("Are you sure you want to delete this draft?")) {
      return;
    }

    axios
      .delete(`/api/student/${studentNumber}/case/${caseId}/draft/${draftId}`, {
        withCredentials: false,
      })
      .then(() => {
        loadDrafts();
      })
      .catch((err) => {
        console.error("Error deleting draft:", err);
        alert("Failed to delete draft. Please try again.");
      });
  }

  if (loading) {
    return (
      <Card className="mt-3">
        <Card.Body className="text-center">
          <Spinner animation="border" size="sm" />
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card className="mt-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>
            <b>Draft Submissions</b>
          </span>
          <Button size="sm" variant="primary" onClick={handleCreateNew}>
            <FontAwesomeIcon icon={faPlus} /> New Draft
          </Button>
        </Card.Header>
        <Card.Body style={{ padding: 0 }}>
          {drafts.length === 0 ? (
            <div className="text-center text-muted p-3">
              <small>
                No drafts yet. Create one to work on your submission.
              </small>
            </div>
          ) : (
            <ListGroup
              variant="flush"
              style={{ maxHeight: "240px", overflowY: "auto" }}
            >
              {drafts.map((draft, index) => (
                <ListGroup.Item
                  key={index}
                  className="d-flex justify-content-between align-items-center"
                >
                  <div style={{ flex: 1 }}>
                    <div>
                      <b>
                        {draft.organName || draft.name || `Draft ${index + 1}`}
                      </b>
                    </div>
                    <small className="text-muted">
                      {draft.segmentationData && (
                        <>
                          📊 {draft.segmentationData.dimensionsX}×
                          {draft.segmentationData.dimensionsY}×
                          {draft.segmentationData.dimensionsZ}
                          {" • "}
                          {(
                            draft.segmentationData.compressedSize / 1024
                          ).toFixed(1)}{" "}
                          KB
                        </>
                      )}
                      {draft.updatedAt &&
                        ` • ${new Date(
                          draft.updatedAt
                        ).toLocaleDateString()} ${new Date(
                          draft.updatedAt
                        ).toLocaleTimeString()}`}
                    </small>
                  </div>
                  <div>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="me-2"
                      onClick={() => handleEditDraft(draft)}
                      title="Edit draft"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => handleDeleteDraft(draft.id)}
                      title="Delete draft"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Card.Body>
      </Card>
    </>
  );
}
